// Referencias de mercado sobre TanStack Query. Mismo patrón que
// `hooks/useInformes.ts`: la cache de la query es la fuente de verdad y los
// filtros van DENTRO de la key.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ajustarReferencia,
  corregirComparable,
  descartarFichaDelCatalogo,
  descartarReferencia,
  enlazarReferencias,
  fetchCatalogo,
  fetchCatalogosDeInformesParaReferencias,
  fetchCatalogosDelPaso2,
  fetchInformesParaReferencias,
  fetchPapelera,
  fetchPuntosDelCatalogo,
  fetchReferenciasDelInforme,
  restaurarReferencia,
  vaciarPapelera,
  validarFichaDelCatalogo,
  validarReferencia,
  type ClaseDeEvidencia,
  type CorreccionDeComparable,
  type FiltrosDeInformesParaReferencias,
  type FiltrosDelCatalogo,
  type IdentidadDeComparable,
  type PaginaDelCatalogo,
} from "@/lib/referencias";

export const REFERENCIAS_KEY = ["referencias"] as const;

/**
 * El listado del paso 1: a qué informe se le enlazan referencias.
 *
 * Los filtros van dentro de la queryKey porque filtra y pagina el BACKEND: con
 * una key por combinación, volver a un filtro ya visitado no lo vuelve a pedir.
 *
 * `placeholderData` conserva lo anterior mientras llega lo nuevo. Acá pesa más
 * que en otros listados: esta pantalla acumula lotes con "cargar más", así que
 * sin él cada clic vaciaría la tabla que el usuario está leyendo.
 */
export function useInformesParaReferencias(filtros: FiltrosDeInformesParaReferencias = {}) {
  return useQuery({
    queryKey: [...REFERENCIAS_KEY, "informes", filtros],
    queryFn: () => fetchInformesParaReferencias(filtros),
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Los controles del filtro del paso 1: el conteo de cada chip y las opciones
 * de comuna, cliente y ejecutivo.
 *
 * SIN FILTROS EN LA KEY, al revés que el listado: no dependen de lo filtrado.
 * Es justamente lo que los vuelve útiles —el chip dice cuántos hay antes de
 * apretarlo— y lo que deja pedirlos una sola vez por sesión en lugar de en
 * cada tecla del buscador.
 */
export function useCatalogosDeInformesParaReferencias() {
  return useQuery({
    queryKey: [...REFERENCIAS_KEY, "informes", "catalogos"],
    queryFn: fetchCatalogosDeInformesParaReferencias,
    // Cambian cuando alguien crea o acepta un informe, que no pasa mientras
    // esta pantalla está abierta. Refetchearlos al volver a la pestaña sería
    // gastar cuatro consultas para redibujar los mismos números.
    staleTime: 5 * 60 * 1000,
  });
}

/* ---------------------------------------------------------------------------
   PASO 2: el catálogo y las referencias enlazadas.
--------------------------------------------------------------------------- */

/**
 * El catálogo de mercado para un informe.
 *
 * La CLASE va en la key igual que el resto de los filtros: las dos pestañas son
 * dos consultas distintas, y compartir cache haría que cambiar de pestaña
 * mostrara por un instante las fichas de la otra.
 */
export function useCatalogoDeComparables(
  informeId: number | null,
  filtros: FiltrosDelCatalogo = {},
) {
  return useQuery({
    queryKey: [...REFERENCIAS_KEY, "catalogo", informeId, filtros],
    queryFn: () => fetchCatalogo(informeId as number, filtros),
    enabled: informeId !== null,
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Todas las coordenadas del filtro, para el mapa y el gráfico.
 *
 * Va en una consulta APARTE del listado y no en la misma, aunque compartan
 * filtros. El listado se vuelve a pedir al pasar de página y al pedir "Cargar
 * más"; los puntos no cambian con eso, y pegados se traerían miles de fichas de
 * nuevo cada vez que alguien agrega seis a la tabla.
 *
 * Por eso la clave EXCLUYE la paginación: dos páginas del mismo filtro
 * comparten la respuesta en caché.
 */
export function usePuntosDelCatalogo(
  informeId: number | null,
  filtros: FiltrosDelCatalogo = {},
) {
  const { pagina: _pagina, porPagina: _porPagina, ...sinPaginar } = filtros;

  return useQuery({
    queryKey: [...REFERENCIAS_KEY, "puntos", informeId, sinPaginar],
    queryFn: () => fetchPuntosDelCatalogo(informeId as number, sinPaginar),
    enabled: informeId !== null,
    // Mantener la nube anterior mientras llega la nueva: sin esto el mapa
    // parpadea a vacío en cada movimiento del filtro, y un mapa que se vacía se
    // lee como "no hay comparables" y no como "estoy cargando".
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Los catálogos de los filtros.
 *
 * `staleTime` alto porque cambian cuando cambia el CATÁLOGO —o sea, cuando el
 * recorrido incorpora fichas nuevas—, no cuando el usuario mueve un filtro. Sin
 * él, cada movimiento del slider recontaría las comunas.
 */
export function useCatalogosDelPaso2(
  informeId: number | null,
  clase: ClaseDeEvidencia,
  incluirIncompletas = false,
) {
  return useQuery({
    // La casilla va en la clave: con ella puesta los contadores incluyen los
    // centroides de comuna, y compartir la caché devolvería los de una a quien
    // tiene la otra.
    queryKey: [...REFERENCIAS_KEY, "catalogos", informeId, clase, incluirIncompletas],
    queryFn: () => fetchCatalogosDelPaso2(informeId as number, clase, incluirIncompletas),
    enabled: informeId !== null,
    staleTime: 5 * 60 * 1000,
    // Al prender o apagar la casilla las opciones se conservan mientras llegan
    // las nuevas —los selectores y la escala de años no parpadean a vacío—,
    // pero SOLO dentro de la misma clase: las comunas de las ofertas no son las
    // de las transacciones.
    placeholderData: (anterior) => (anterior?.clase === clase ? anterior : undefined),
  });
}

/** Las referencias que el informe ya hizo suyas. */
export function useReferenciasDelInforme(informeId: number | null) {
  return useQuery({
    queryKey: [...REFERENCIAS_KEY, "enlazadas", informeId],
    queryFn: () => fetchReferenciasDelInforme(informeId as number),
    enabled: informeId !== null,
  });
}

/**
 * "Agregar N al informe".
 *
 * Al terminar invalida EL CATÁLOGO además del listado de enlazadas, y eso no es
 * exceso de celo: lo que se acaba de enlazar deja de ofrecerse, así que sin
 * invalidarlo las fichas seguirían en la grilla y el usuario podría volver a
 * marcarlas.
 */
export function useEnlazarReferencias(informeId: number | null, incluirIncompletas = false) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (referencias: IdentidadDeComparable[]) =>
      enlazarReferencias(informeId as number, referencias, incluirIncompletas),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "catalogo", informeId] });
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "enlazadas", informeId] });
      // El paso 1 muestra el conteo de referencias por informe: sin esto, volver
      // atrás mostraría el número viejo.
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "informes"] });
    },
  });
}

/* ---------------------------------------------------------------------------
   Decidir sobre una ficha DEL CATÁLOGO, antes de agregarla.

   Las dos de abajo se dirigen por (clase, origen) porque la ficha todavía no
   tiene fila de este lado: la crea el propio pedido.
--------------------------------------------------------------------------- */

/**
 * Validar o desvalidar una ficha del catálogo.
 *
 * PARCHEA LA CACHÉ EN VEZ DE INVALIDARLA, y es la única mutación del módulo que
 * lo hace. Invalidar dispara un refetch del listado entero —seis fichas, sus
 * agregados y la mediana— para cambiar un booleano; con `placeholderData` la
 * pantalla seguiría mostrando el estado viejo hasta que vuelva la respuesta, y
 * el botón que la persona acaba de apretar tardaría en reflejarlo. Nada más
 * cambia al validar: la ficha no se va del listado ni se mueve de lugar.
 *
 * Parchea TODAS las páginas cacheadas del informe y no solo la visible: los
 * filtros van dentro de la clave, así que volver a un filtro ya visitado lo
 * sirve de caché — y sin esto lo serviría con el estado anterior.
 */
export function useValidarFichaDelCatalogo(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ ficha, validada }: { ficha: IdentidadDeComparable; validada: boolean }) =>
      validarFichaDelCatalogo(informeId as number, ficha, validada),
    onSuccess: (_referencia, { ficha, validada }) => {
      cliente.setQueriesData<PaginaDelCatalogo>(
        { queryKey: [...REFERENCIAS_KEY, "catalogo", informeId] },
        (pagina) => {
          // La clase importa: `listing.id` y `transaccion.id` son secuencias
          // independientes, así que sin este corte validar la transacción 101
          // pintaría la oferta 101 en la caché de la otra pestaña.
          if (!pagina || pagina.clase !== ficha.clase) return pagina;

          const sinEsta = pagina.validadas.filter((id) => id !== ficha.origen_id);

          return {
            ...pagina,
            validadas: validada ? [...sinEsta, ficha.origen_id] : sinEsta,
          };
        },
      );
    },
  });
}

/**
 * Descartar una ficha del catálogo: a la papelera sin haberla agregado.
 *
 * ACÁ SÍ SE INVALIDA, al revés que validar: la ficha SALE del listado, así que
 * cambian el total, la mediana, la paginación y el contador de la papelera. Un
 * parche a mano tendría que recalcular todo eso en el navegador, que es
 * exactamente el cálculo que el servidor ya hace.
 */
export function useDescartarFichaDelCatalogo(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (ficha: IdentidadDeComparable) =>
      descartarFichaDelCatalogo(informeId as number, ficha),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "catalogo", informeId] });
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "papelera", informeId] });
    },
  });
}

/* ---------------------------------------------------------------------------
   Decidir sobre lo ya enlazado.

   Las tres mutaciones invalidan LO ENLAZADO Y LA PAPELERA, porque una fila se
   mueve de una a la otra: refrescar solo la lista de origen dejaría la de
   destino mostrando el estado anterior.
--------------------------------------------------------------------------- */

/** Las referencias que este informe descartó y todavía puede recuperar. */
export function usePapeleraDeReferencias(informeId: number | null) {
  return useQuery({
    queryKey: [...REFERENCIAS_KEY, "papelera", informeId],
    queryFn: () => fetchPapelera(informeId as number),
    enabled: informeId !== null,
  });
}

function refrescarLasDosListas(cliente: ReturnType<typeof useQueryClient>, informeId: number | null) {
  cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "enlazadas", informeId] });
  cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "papelera", informeId] });
}

export function useValidarReferencia(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, validada }: { id: number; validada: boolean }) =>
      validarReferencia(informeId as number, id, validada),
    onSuccess: () => refrescarLasDosListas(cliente, informeId),
  });
}

export function useDescartarReferencia(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => descartarReferencia(informeId as number, id),
    onSuccess: () => {
      refrescarLasDosListas(cliente, informeId);
      // El paso 1 muestra el conteo de referencias VIGENTES por informe.
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "informes"] });
    },
  });
}

export function useRestaurarReferencia(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => restaurarReferencia(informeId as number, id),
    onSuccess: () => {
      refrescarLasDosListas(cliente, informeId);
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "informes"] });
    },
  });
}

/**
 * Con qué categorías se homologa una referencia.
 *
 * NO toca el catálogo ni el paso 1: homologar corrige lo que el informe ya se
 * llevó, así que no cambia qué fichas se ofrecen ni cuántas tiene enlazadas.
 * Alcanza con refrescar las dos listas de referencias.
 */
export function useAjustarReferencia(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, categoriaIds }: { id: number; categoriaIds: number[] }) =>
      ajustarReferencia(informeId as number, id, categoriaIds),
    onSuccess: () => refrescarLasDosListas(cliente, informeId),
  });
}

/**
 * Corregir los DATOS de un comparable.
 *
 * INVALIDA TAMBIÉN EL CATÁLOGO, al revés que homologar, y esa es la diferencia
 * de fondo entre las dos acciones: homologar corrige lo que ESTE informe se
 * llevó, corregir arregla el DATO —que es de todos— y el listado del catálogo
 * ya lo estaría mostrando sin la corrección puesta.
 */
export function useCorregirComparable(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, correccion }: { id: number; correccion: CorreccionDeComparable }) =>
      corregirComparable(informeId as number, id, correccion),
    onSuccess: () => {
      refrescarLasDosListas(cliente, informeId);
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "catalogo", informeId] });
    },
  });
}

/**
 * Vaciar la papelera.
 *
 * Invalida además EL CATÁLOGO, y eso no es exceso de celo: al borrar las filas,
 * esas fichas vuelven a ser ofrecibles. Sin invalidarlo, la grilla seguiría
 * escondiéndolas hasta la próxima recarga.
 */
export function useVaciarPapelera(informeId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: () => vaciarPapelera(informeId as number),
    onSuccess: () => {
      refrescarLasDosListas(cliente, informeId);
      cliente.invalidateQueries({ queryKey: [...REFERENCIAS_KEY, "catalogo", informeId] });
    },
  });
}
